// Shared visual language for City Explorer's live maps. Ported from the
// Connecticut Folium goldens (ct_school_district_map / opportunity maps):
// continuous red→white→green polygon fill, pale→dark green sequential
// ramps, and charter pins colored by projected enrollment trend.

export const TREND_COLORS = {
  Growing: '#2a9d2a',
  Stable: '#e6c200',
  Declining: '#cc2222',
};

export const NO_DATA_COLOR = '#aaaaaa';
export const NEW_SCHOOL_RING = '#333333';

// Hover/legend text for Stable. The pin fill (#e6c200) is too light on white;
// CT uses #888888 for "Stable" inside district hover cards.
export const STABLE_TEXT_COLOR = '#9a7b00';
export const STABLE_CARD_TEXT_COLOR = '#888888';

export const DIVERGING_LOW = '#dc0000';
export const DIVERGING_MID = '#dcdcdc';
export const DIVERGING_HIGH = '#00be00';
export const SEQUENTIAL_GREEN_LOW = '#d9f0d0';
export const SEQUENTIAL_GREEN_HIGH = '#0b5c28';

export const TREND_SCALE_ABS = 0.15;

export function trendColor(status) {
  return TREND_COLORS[status] || NO_DATA_COLOR;
}

export function trendTextColor(status) {
  if (status === 'Growing') return TREND_COLORS.Growing;
  if (status === 'Declining') return TREND_COLORS.Declining;
  if (status === 'Stable') return STABLE_CARD_TEXT_COLOR;
  return STABLE_CARD_TEXT_COLOR;
}

export const DISTRICT_DEFAULT_STYLE = {
  color: '#ffffff',
  weight: 1,
  opacity: 1,
  fillOpacity: 0.55,
};

export const DISTRICT_HOVER_STYLE = {
  weight: 1.5,
  fillOpacity: 0.75,
};

function hexToRgb(hex) {
  const raw = hex.replace('#', '');
  const n = parseInt(raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex({ r, g, b }) {
  const to = (v) => v.toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function lerpHex(a, b, t) {
  const clamped = Math.max(0, Math.min(1, t));
  const from = hexToRgb(a);
  const to = hexToRgb(b);
  return rgbToHex({
    r: Math.round(from.r + (to.r - from.r) * clamped),
    g: Math.round(from.g + (to.g - from.g) * clamped),
    b: Math.round(from.b + (to.b - from.b) * clamped),
  });
}

/** Red → gray → green, scaled to ±maxAbs (CT uses ±15%). */
export function divergingTrendFill(pct, maxAbs = TREND_SCALE_ABS) {
  if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) {
    return NO_DATA_COLOR;
  }
  const clamped = Math.max(-maxAbs, Math.min(maxAbs, Number(pct)));
  const t = (clamped + maxAbs) / (2 * maxAbs);
  if (t < 0.5) return lerpHex(DIVERGING_LOW, DIVERGING_MID, t * 2);
  return lerpHex(DIVERGING_MID, DIVERGING_HIGH, (t - 0.5) * 2);
}

/** Pale green → dark green across an observed [min, max] range. */
export function sequentialGreenFill(value, min, max) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return NO_DATA_COLOR;
  }
  if (min === max || !Number.isFinite(min) || !Number.isFinite(max)) {
    return lerpHex(SEQUENTIAL_GREEN_LOW, SEQUENTIAL_GREEN_HIGH, 0.5);
  }
  const t = (Number(value) - min) / (max - min);
  return lerpHex(SEQUENTIAL_GREEN_LOW, SEQUENTIAL_GREEN_HIGH, t);
}

export const SEQUENTIAL_RED_LOW = '#fde8e8';
export const SEQUENTIAL_RED_HIGH = '#a50f15';

/** Pale → dark red across [min, max], for "more is worse" shares. */
export function sequentialRedFill(value, min, max) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return NO_DATA_COLOR;
  }
  if (min === max || !Number.isFinite(min) || !Number.isFinite(max)) {
    return lerpHex(SEQUENTIAL_RED_LOW, SEQUENTIAL_RED_HIGH, 0.5);
  }
  return lerpHex(SEQUENTIAL_RED_LOW, SEQUENTIAL_RED_HIGH, (Number(value) - min) / (max - min));
}

export function isNewCharter(school) {
  return school?.enrollmentComparison === null || school?.enrollmentComparison === undefined;
}

export function charterPinStyle(school, statusKey = 'enrollmentProjectionStatus') {
  if (isNewCharter(school)) {
    return {
      radius: 8,
      color: NEW_SCHOOL_RING,
      weight: 3,
      fillColor: '#ffffff',
      fillOpacity: 1,
      opacity: 1,
    };
  }
  return {
    radius: 8,
    color: '#ffffff',
    weight: 2,
    fillColor: trendColor(school?.[statusKey]),
    fillOpacity: 0.95,
    opacity: 1,
  };
}

/** Pins shaded by the 5-year historical enrollment trend (risk map). */
export function charterHistoricalPinStyle(school) {
  return { ...charterPinStyle(school, 'enrollmentTrendStatus'), radius: 9 };
}

export const CARTO_ATTRIBUTION =
  '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export const FALLBACK_TILE_URL_TEMPLATE =
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

export const DESERT_GAP_LOW = '#fde8e8';
export const DESERT_GAP_HIGH = '#ff0000';
export const OVERLAP_LOW = '#1a9850';
export const OVERLAP_HIGH = '#d73027';

export function desertGapFill(area, minArea, maxArea) {
  if (area === null || area === undefined || !Number.isFinite(Number(area))) {
    return DESERT_GAP_HIGH;
  }
  if (!Number.isFinite(minArea) || !Number.isFinite(maxArea) || minArea === maxArea) {
    return lerpHex(DESERT_GAP_LOW, DESERT_GAP_HIGH, 0.6);
  }
  const t = (Number(area) - minArea) / (maxArea - minArea);
  return lerpHex(DESERT_GAP_LOW, DESERT_GAP_HIGH, t);
}

export function overlapFill(count, maxCount) {
  if (!Number.isFinite(Number(count)) || Number(count) < 1) return OVERLAP_LOW;
  if (!Number.isFinite(maxCount) || maxCount <= 1) return lerpHex(OVERLAP_LOW, OVERLAP_HIGH, 0.3);
  const t = (Number(count) - 1) / (maxCount - 1);
  return lerpHex(OVERLAP_LOW, OVERLAP_HIGH, t);
}
